import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import Table, { TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { getCancellationColumns } from "../_components/CancellationTableColumns";
import { withGlobalEmptyStates } from "@/components/Table/utils";
import { EVENT_REGISTRATION_HISTORY_QUERY } from "../../queries";
import { useQuery } from "@apollo/client";
import { toFormat } from "@/lib/dateHelpers";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { ExpandedRegistrationRow } from "../_components/ExpandedRegistrationRow";
import { getHours, getTime } from "@/lib/dateHelpers";
import { EventRegistration, EventRegistrationHistoryData, PersonEventRegistration } from "../types";
import { OrderStage } from "@/pages/billing/types";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import SearchInput from "@/components/SearchInput";
import DateSelect from "@/components/DateSelect";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { useNavigate, Path } from "@/router";
import MultiSelect from "@/components/MultiSelect";
import { GET_EVENT_TYPES } from "@/pages/events/queries";
import { DateTime } from "luxon";
import { Row } from "@tanstack/react-table";
import { EventType } from "@/pages/events/types";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { matchesEventTypeFilter } from "@/pages/events/[eventSlug]/utils";
import { usePersonEventsAccess } from "../_usePersonEventsAccess";

function formatTableData(registrations: EventRegistration[]): PersonEventRegistration[] {
  return registrations.map((registration) => ({
    id: registration.id,
    orderId: registration.order?.id,
    eventSlug: registration.event.slug,
    eventTitle: registration.event.title,
    eventType: registration.event.eventType,
    activityType: registration.event.activityType,
    status: registration.event.status,
    startsAt: registration.event.startsAt,
    endsAt: registration.event.endsAt,
    registeredAt: toFormat(registration.registeredAt),
    canceledAt: registration.canceledAt,
    totalPaid: registration.order
      ? centsToDollarsFormatted(registration.order.totalRefunded || 0) || undefined
      : undefined,
    facility: registration.event.location || registration.event.facility?.name || "TBD",
    orderStatus: registration.order?.status as OrderStage,
    paymentStatus: registration.order?.paymentStatus,
    eventCancelledAt: registration.event.cancelledAt,
    sessions: registration.sessions?.map((session) => ({
      id: session.id,
      title: session.title,
      startsAt: session.startsAt,
      endsAt: session.endsAt,
      duration: getHours(session.duration) ?? "",
      startTime: getTime(session.startsAt, undefined, registration.event.timezone),
      endTime: getTime(session.endsAt, undefined, registration.event.timezone)
    }))
  }));
}

export default function PersonEventCancellationsPage() {
  usePageTitle("/people/events/cancellations", "Event Cancellations");
  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();
  const { pgaId } = useParams();
  const { loaded, canViewPersonEvents } = usePersonEventsAccess();
  const skipEventQueries = !loaded || !canViewPersonEvents || !pgaId;

  const { data, loading, error } = useQuery<EventRegistrationHistoryData>(EVENT_REGISTRATION_HISTORY_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipEventQueries
  });

  const registrations = data?.eventRegistrationHistory || [];
  // Filter for only canceled registrations
  const canceledRegistrations = registrations.filter((registration) => registration.canceledAt);
  const eventRegistrations = formatTableData(canceledRegistrations);

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "eventTitle"
  });
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [selectedDates, setSelectedDates] = useState<any>({ from: null, to: null });
  const { data: eventTypesData } = useQuery<{ eventTypes: EventType[] }>(GET_EVENT_TYPES, {
    skip: skipEventQueries
  });

  const filterState = {
    eventType: selectedEventTypes.length > 0,
    status: selectedValues.length > 0,
    dates: !!(selectedDates?.from && selectedDates?.to)
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setSelectedEventTypes([]);
    setSelectedValues([]);
    setSelectedDates({ from: null, to: null });
    clearSearch();
  };

  const filteredData = useMemo(() => {
    let filtered = eventRegistrations;

    if (debouncedSearch) {
      filtered = filtered.filter((row) => row.eventTitle.toLowerCase().includes(debouncedSearch.toLowerCase()));
    }

    if (selectedEventTypes.length > 0) {
      filtered = filtered.filter((row) => matchesEventTypeFilter(row, selectedEventTypes));
    }

    if (selectedValues.length > 0) {
      filtered = filtered.filter((row) => selectedValues.includes(row.status));
    }

    if (selectedDates.from && selectedDates.to) {
      filtered = filtered.filter((row) => {
        const startsAt = DateTime.fromISO(row.startsAt);
        const endsAt = DateTime.fromISO(row.endsAt);
        return (
          startsAt >= DateTime.fromISO(selectedDates.from).startOf("day") &&
          endsAt <= DateTime.fromISO(selectedDates.to).endOf("day")
        );
      });
    }

    return filtered;
  }, [eventRegistrations, debouncedSearch, selectedEventTypes, selectedValues, selectedDates]);

  if (error) return <div>Error: {error.message}</div>;

  const onClickRow = (row: Row<PersonEventRegistration>) => {
    navigate(
      `/events/${row.original.eventSlug}/registrations/${row.original.id}?return_to=/people/${pgaId}/events/cancellations` as Path
    );
  };

  const sessionColumnDefs = (row: Row<PersonEventRegistration>) => [
    {
      accessorKey: "startsAt",
      header: "Start Date",
      enableSorting: false,
      meta: {
        className: getStickyColumnClasses(!!row.original.sessions?.length)
      },
      cell: ({ row }: { row: Row<PersonEventRegistration> }) => {
        return <div>{toFormat(row.original.startsAt)}</div>;
      }
    },
    {
      accessorKey: "endsAt",
      header: "End Date",
      enableSorting: false,
      cell: ({ row }: { row: Row<PersonEventRegistration> }) => {
        return <div>{toFormat(row.original.endsAt)}</div>;
      }
    },
    { accessorKey: "startTime", header: "Start Time", enableSorting: false },
    { accessorKey: "endTime", header: "End Time", enableSorting: false },
    { accessorKey: "title", header: "Day of the Week", enableSorting: false },
    { accessorKey: "duration", header: "Duration", enableSorting: false }
  ];

  return (
    <div className="w-full">
      <Menu />
      <div className="pt-12" />
      <Table
        data={filteredData}
        loading={loading}
        renderExpandedRow={({ row }) => (
          <ExpandedRegistrationRow row={row} columnDefsSessions={sessionColumnDefs(row)} />
        )}
        renderControls={() => (
          <TableControls
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Event"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            <div data-name="eventType">
              <MultiSelect
                label="Event Type"
                selectedValues={selectedEventTypes}
                options={
                  eventTypesData?.eventTypes.map((eventType) => ({
                    value: eventType.name,
                    label: eventType.name
                  })) || []
                }
                onSelect={(value, checked) => {
                  const eventTypes = checked
                    ? [...selectedEventTypes, value]
                    : selectedEventTypes.filter((v) => v !== value);
                  setSelectedEventTypes(eventTypes);
                }}
                onClear={() => {
                  setSelectedEventTypes([]);
                }}
              />
            </div>
            <div data-name="status">
              <MultiSelect
                label="Stage"
                selectedValues={selectedValues}
                options={[
                  { value: "Active", label: "Active" },
                  { value: "Pending", label: "Pending" },
                  { value: "Cancelled", label: "Canceled" },
                  { value: "Completed", label: "Completed" },
                  { value: "Draft", label: "Draft" }
                ]}
                onSelect={(value, checked) => {
                  const status = checked ? [...selectedValues, value] : selectedValues.filter((v) => v !== value);
                  setSelectedValues(status);
                }}
                onClear={() => {
                  setSelectedValues([]);
                }}
              />
            </div>
            <div data-name="dates" data-filter-size="custom">
              <DateSelect
                label="Session Date Range"
                useModal={true}
                useDefaultShortcuts={true}
                placeholder="Show All"
                value={
                  selectedDates?.from && selectedDates?.to
                    ? {
                        startDate: selectedDates.from,
                        endDate: selectedDates.to
                      }
                    : { startDate: null, endDate: null }
                }
                onChange={(e) => {
                  if (typeof e === "object" && e !== null) {
                    setSelectedDates({ from: e.startDate, to: e.endDate });
                  }
                }}
              />
            </div>
          </TableControls>
        )}
        onClickRow={onClickRow}
        columns={withGlobalEmptyStates(getCancellationColumns({ setMenu, navigate, pgaId: pgaId! }))}
        hasActiveFilters={activeFilterCount > 0}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Event Cancellations"
            caption="This person has not canceled any event registrations."
            iconName="event_busy"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={filteredData.length} />}
      />
    </div>
  );
}
